import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { Form } from '@/components/ui/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useUsers } from '@/hooks/useUsers';
import { useClients } from '@/hooks/useClients';
import { useGroups } from '@/hooks/useGroups';
import { useUserResources } from '@/hooks/useUserResources';
import { UserFormFields } from './UserFormFields';
import { EditUserFormActions } from './EditUserFormActions';
import { UserResourcesTab } from './UserResourcesTab';
import { User, Package } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const userSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  email: z.string().email('Email inválido'),
  phone: z.string().optional(),
  department: z.string().optional(),
  role: z.string().min(1, 'Função é obrigatória'),
  client_id: z.string().optional(),
  client_ids: z.array(z.string()).optional(),
  status: z.enum(['active', 'inactive']),
  groups: z.array(z.string()).optional(),
});

type UserFormData = z.infer<typeof userSchema>;

interface UserData {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  department: string | null;
  role: string;
  client_id: string | null;
  client_ids?: string[];
  status: 'active' | 'inactive';
}

interface EditUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserData;
}

export const EditUserDialog = ({ open, onOpenChange, user }: EditUserDialogProps) => {
  const { updateUser, getUserGroups } = useUsers();
  const { clients } = useClients();
  const { groups } = useGroups();
  const { ativos, licencas } = useUserResources(user?.id);

  const totalRecursos = (ativos?.length || 0) + (licencas?.length || 0);

  const form = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      department: '',
      role: 'user',
      client_id: '',
      client_ids: [],
      status: 'active',
      groups: [],
    },
  });

  useEffect(() => {
    if (user && open) {
      const loadUserData = async () => {
        try {
          const [userGroups, { data: freshUser }] = await Promise.all([
            getUserGroups(user.id),
            supabase
              .from('users')
              .select('name, email, phone, department, role, client_id, client_ids, status')
              .eq('id', user.id)
              .maybeSingle(),
          ]);

          const freshClientIds = Array.isArray(freshUser?.client_ids)
            ? (freshUser.client_ids as string[])
            : [];

          const formData = {
            name: freshUser?.name || user.name || '',
            email: freshUser?.email || user.email || '',
            phone: freshUser?.phone || user.phone || '',
            department: freshUser?.department || user.department || '',
            role: freshUser?.role || user.role || 'user',
            client_id: freshUser?.client_id || user.client_id || '',
            client_ids: freshClientIds,
            status: (freshUser?.status || user.status || 'active') as 'active' | 'inactive',
            groups: userGroups.map(g => g.id),
          };
          
          form.reset(formData);
          
        } catch (error) {
          console.error('EditUserDialog - Error loading user data:', error);
          
          const formData = {
            name: user.name || '',
            email: user.email || '',
            phone: user.phone || '',
            department: user.department || '',
            role: user.role || 'user',
            client_id: user.client_id || '',
            client_ids: user.client_ids || [],
            status: user.status || 'active' as const,
            groups: [],
          };
          
          form.reset(formData);
        }
      };

      loadUserData();
    }
  }, [user.id, open]);

  const onSubmit = async (data: UserFormData) => {
    const formData = {
      name: data.name,
      email: data.email,
      role: data.role,
      status: data.status,
      phone: data.phone || undefined,
      department: data.department || undefined,
      client_id: data.client_id || undefined,
      client_ids: data.client_ids || [],
      groups: data.groups || [],
    };

    try {
      await updateUser.mutateAsync({ id: user.id, data: formData });
      onOpenChange(false);
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Editar Usuário</DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue="dados" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="dados" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Dados
            </TabsTrigger>
            <TabsTrigger value="recursos" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Recursos
              {totalRecursos > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                  {totalRecursos}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dados" className="flex-1 overflow-y-auto mt-4">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <UserFormFields 
                  control={form.control} 
                  clients={clients} 
                  groups={groups}
                />
                <EditUserFormActions 
                  onCancel={handleCancel}
                  isSubmitting={form.formState.isSubmitting}
                />
              </form>
            </Form>
          </TabsContent>

          <TabsContent value="recursos" className="flex-1 overflow-y-auto mt-4">
            <UserResourcesTab userId={user.id} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
